import {
  createStyleMixin,
  draggableMixin,
  componentNameValidationMixin,
} from '@descope-ui/common/components-mixins';
import { createBaseClass } from '@descope-ui/common/base-classes';
import { compose } from '@descope-ui/common/utils';
import {
  getComponentName,
  injectStyle,
} from '@descope-ui/common/components-helpers';
import clockIcon from './clock.svg';
import { IconClass } from '@descope-ui/descope-icon/class';
import { formatTime } from './helpers';

export const componentName = getComponentName('timer');

const observedAttributes = ['seconds', 'hide-icon', 'paused'];

const BaseClass = createBaseClass({
  componentName,
  baseSelector: ':host > .wrapper',
});

const DEFAULT_INTERVAL = 1000;

class RawTimer extends BaseClass {
  #timeRemains = 0;

  #intervalId;

  static get observedAttributes() {
    return observedAttributes.concat(BaseClass.observedAttributes || []);
  }

  constructor() {
    super();

    this.attachShadow({ mode: 'open' }).innerHTML = `
      <div class="wrapper">
        <descope-icon src="${clockIcon}" class="icon"></descope-icon>
        <div class="timer"></div>
      </div>
		`;

    injectStyle(
      `
      :host {
        display: inline-flex;
        user-select: none;
        -webkit-user-select: none;
      }
      .wrapper {
        display: flex;
        align-items: center;
        flex-direction: row;
        width: 100%;
      }
      .hidden {
        display: none;
      }
    `,
      this,
    );

    this.icon = this.shadowRoot.querySelector('.icon');
    this.timer = this.shadowRoot.querySelector('.timer');
  }

  get seconds() {
    const val = this.getAttribute('seconds');
    const secs = parseInt(val || 0, 10) * 1000;
    return Math.max(0, secs);
  }

  // we use `pause` attribute for allowing preview mode by executing `pause`
  get isPaused() {
    return this.getAttribute('paused') === 'true';
  }

  init() {
    super.init?.();

    this.#timeRemains = this.seconds;
  }

  startInterval() {
    const intervalCycle = () => {
      this.#decreaseInterval();
      if (!this.#timeRemains) this.stop();
      this.#updateDisplay(this.#timeRemains);
    };

    if (this.#timeRemains) {
      this.#intervalId = setInterval(intervalCycle, DEFAULT_INTERVAL);
    } else {
      this.stop();
    }
  }

  stopInterval() {
    clearInterval(this.#intervalId);
  }

  #decreaseInterval() {
    this.#timeRemains = this.#timeRemains - DEFAULT_INTERVAL;
  }

  reset() {
    this.#timeRemains = this.seconds;
    this.#updateDisplay(this.#timeRemains);

    if (this.isPaused) return;

    this.dispatchEvent(new CustomEvent('timer-started', { bubbles: true }));
    this.startInterval();
  }

  stop() {
    this.stopInterval();
    this.#timeRemains = 0;
    this.#updateDisplay(this.#timeRemains);
    this.dispatchEvent(new CustomEvent('timer-ended', { bubbles: true }));
  }

  pause() {
    this.setAttribute('paused', 'true');
    this.stopInterval();
  }

  resume() {
    this.removeAttribute('paused');
    this.startInterval();
  }

  #updateDisplay(val) {
    this.timer.textContent = formatTime(val);
  }

  #toggleIcon(val) {
    this.icon.classList.toggle('hidden', val);
  }

  #handlePause(paused) {
    setTimeout(() => {
      if (paused) {
        this.pause();
      } else {
        // we want to prevent a another interval from starting in case previous interval was not yet cleared
        if (!this.#intervalId) {
          this.resume();
        }
      }
    });
  }

  attributeChangedCallback(attrName, oldValue, newValue) {
    super.attributeChangedCallback?.(attrName, oldValue, newValue);

    if (newValue !== oldValue) {
      if (attrName === 'seconds') {
        this.reset();
      }
      if (attrName === 'hide-icon') {
        this.#toggleIcon(newValue === 'true');
      }
      if (attrName === 'paused') {
        this.#handlePause(newValue === 'true');
      }
    }
  }
}

const { host, icon, timer } = {
  host: { selector: () => ':host' },
  icon: { selector: () => '.icon' },
  timer: { selector: () => '.timer' },
};

export const TimerClass = compose(
  createStyleMixin({
    mappings: {
      fontSize: {},
      iconSize: [
        { ...icon, property: 'width' },
        { ...icon, property: 'height' },
      ],
      fontFamily: {},
      minHeight: {},
      fontWeight: { ...timer },
      lineHeight: { ...timer },
      textColor: { ...timer, property: 'color' },
      gap: {},
      textAlign: { property: 'justify-content' },
      hostWidth: { ...host, property: 'width' },
      hostDirection: { ...host, property: 'direction' },
      iconColor: { ...icon, property: IconClass.cssVarList.fill },
    },
  }),
  draggableMixin,
  componentNameValidationMixin,
)(RawTimer);
